import { YES, NO } from 'utils/constants';
import { mapFormFieldsToApiFormat } from 'utils/mappers';
import { NetworkLabels, ServiceTypeEnum } from 'utils/enums';

export const mobileImagingTypes = [
  { value: 'Rapid', text: 'Rapid' },
  { value: 'VizAl', text: 'Viz.AI' },
  { value: 'Aidoc', text: 'Aidoc' },
];

export const strokeCertificationOptions = [
  { value: 'CSC', text: 'CSC' },
  { value: 'TSC', text: 'TSC' },
  { value: 'PSC', text: 'PSC' },
  { value: 'ASR', text: 'ASR' },
  { value: 'None', text: 'None' },
];

export const hospitalSizeOptions = [
  { value: 'LessThen50Beds', text: '<50 beds' },
  { value: 'Between50And100Beds', text: '50-100 beds' },
  { value: 'Between100And500Beds', text: '100-500 beds' },
  { value: 'MoreThen500Beds', text: '>500 beds' },
];

export const thrombolyticValues = {
  Alteplase: 'Alteplase',
  Tenecteplase: 'Tenecteplase',
};

export const thrombolyticValuesOptions = Object.entries(thrombolyticValues).map(
  ([value, text]) => ({ value, text }),
);

export const networkFields = Object.keys(NetworkLabels).reduce(
  (res, network) => ({ ...res, [network]: `networks.${network}` }),
  {},
);

export const integrationMessageTypes = {
  RECEIVE_ORDERS: 'ReceiveOrders',
  SEND_SUMMARY: 'SendSummary',
  SEND_BILLING: 'SendBilling',
};

export const consultTypeOptions = Object.entries(ServiceTypeEnum).map(
  ([, value]) => ({
    text: value,
    value,
  }),
);

export const targetOptions = [
  {
    text: 'R1',
    value: 'R1',
    allowedFor: [integrationMessageTypes.SEND_BILLING],
  },
  {
    text: 'HL7ORM',
    value: 'Hl7ORM',
    allowedFor: [integrationMessageTypes.RECEIVE_ORDERS],
  },
  {
    text: 'Custom File',
    value: 'CustomFile',
    allowedFor: [integrationMessageTypes.RECEIVE_ORDERS],
  },
];

export const getTargetOptionsForType = (type) =>
  targetOptions
    .filter((opt) => opt.allowedFor.includes(type))
    .map(({ text, value }) => ({ text, value }));

export const integrationFields = {
  facilityIntegrationConfigurations: 'facilityIntegrationConfigurations',
};

export const facilityFields = {
  active: 'active',
  name: 'name',
  street: 'address.streetAddress',
  city: 'address.locality',
  state: 'address.region',
  zipCode: 'address.postalCode',
  timezone: 'timezone',
  phone: 'edNumber',
  hospitalMainNumber: 'hospitalMainNumber',
  itPhone: 'itPhoneNumber',
  callCenterPhone: 'callCenterPhoneNumber',
  autoFax: 'autoFax',
  fax: 'primaryFaxNumber',
  secondaryFaxNumber: 'secondaryFaxNumber',
  emrName: 'emrName',
  emrLink: 'emrLink',
  directBeamIn: 'directBeamIn',
  eegSoftware: 'eegSoftware',
  eegSoftwareLink: 'eegSoftwareLink',
  strokeHub: 'strokeHub',
  neuroHospitalHub: 'neuroHospitalHub',
  eegPedsEegHub: 'eegPedsEegHub',
  radiology: 'radiology',
  radiologyLink: 'radiologyLink',
  sharedSignout: 'sharedSignout',
  sharedSignoutLink: 'sharedSignoutLink',
  camera: 'camera',
  cameraLink: 'cameraLink',
  mobileImagings: 'mobileImagings',
  note: 'note',
  isIntegrated: 'isIntegrated',
  hL7FacilityCode: 'hL7FacilityCode',
  strokeCertification: 'strokeCertification',
  hospitalSize: 'hospitalSize',
  codeFromSystem: 'inheritSiteCodeFromSystem',
  siteCode: 'siteCode',
  thrombolytic: 'thrombolytic',
  strokeCoverage: 'strokeCoverage',
  inpatientCoverage: 'inpatientCoverage',
  eegReader: 'eegReader',
  eegServices: 'eegServices',
  workflowDeviations: 'workflowDeviations',
  lvoProtocol: 'lvoProtocol',
  isCpt: 'isCpt',
  isExtWindowProtocol: 'isExtWindowProtocol',
  facilityIntegrationConfigurations: 'facilityIntegrationConfigurations',
  ...networkFields,
};

// Convert facility before sending a request to the API
export const mapFacilityToApiFormat = (facilityValues) => {
  const { providers, facilityIntegrationCodeMappings, ...facility } = facilityValues;
  return {
    ...mapFormFieldsToApiFormat({
      ...facility,
      isIntegrated: Boolean(facility.isIntegrated),
      directBeamIn: facility.directBeamIn === YES,
      isCpt: facility.isCpt === YES,
      isExtWindowProtocol: facility.isExtWindowProtocol === YES,
    }),
    networks: Object.entries(facility.networks).reduce(
      (res, [key, network]) =>
        network
          ? [
              ...res,
              {
                networkType: key,
                networkId: network,
              },
            ]
          : res,
      [],
    ),
    mobileImagings: Object.values(facility.mobileImagings),
    facilityIntegrationConfigurations: Object.entries(
      facility.facilityIntegrationConfigurations || {},
    )
      .filter(([, config]) => config?.enabled)
      .map(([messageType, config]) => {
        const formatted = {
          messageType,
          consultTypes: config.consultTypes,
        };

        if (messageType === integrationMessageTypes.SEND_SUMMARY) {
          formatted.target = 'HL7ORM';
        } else if (config.target) {
          formatted.target = config.target;
        }

        return formatted;
      }),
      facilityIntegrationCodeMappings: facilityIntegrationCodeMappings?.length
      ? facilityIntegrationCodeMappings.map((m) => ({
          orderId: m.orderId?.trim() || null,
          orderText: m.orderText?.trim() || null,
          services: m.services?.length ? m.services : null,
        }))
      : null,
  };
};

// Convert facility before autofill form
export const mapFacilityToForm = (facility) => ({
  ...facility,
  directBeamIn:
    (facility.directBeamIn === false && NO) ||
    (facility.directBeamIn ? YES : null),
  isCpt: (facility.isCpt === false && NO) || (facility.isCpt ? YES : null),
  isExtWindowProtocol:
    (facility.isExtWindowProtocol === false && NO) ||
    (facility.isExtWindowProtocol ? YES : null),
  networks: facility.networks.reduce(
    (res, network) => ({
      ...res,
      [network.networkType]: network.network ? network.network.id : null,
    }),
    {},
  ),
  providerIds: facility.providers?.map((p) => p.id) ?? [],
  facilityIntegrationCodeMappings:
    facility.facilityIntegrationCodeMappings?.map((m) => ({
      orderId: m.orderId ?? '',
      orderText: m.orderText ?? '',
      services: m.services ?? [],
    })) ?? [],
  facilityIntegrationConfigurations:
    facility.facilityIntegrationConfigurations?.reduce(
      (acc, config) => ({
        ...acc,
        [config.messageType]: {
          enabled: true,
          consultTypes: config.consultTypes ?? [],
          target:
            config.messageType === integrationMessageTypes.SEND_SUMMARY
              ? 'HL7ORM'
              : (config.target ?? ''),
        },
      }),
      {
        ReceiveOrders: { enabled: false, consultTypes: [], target: '' },
        SendSummary: { enabled: false, consultTypes: [] },
        SendBilling: { enabled: false, consultTypes: [], target: '' },
      },
    ),
});
